import axios from 'axios';

import { KC_REQUEST_ID_HEADER } from '../../constants/requestId';
import { STATUS_CODE } from '../../constants/status';
import { isDynamicRouteSegment } from '../../utils/route';
import captureAxiosError from './captureAxiosError';
import CustomAxiosError from './CustomAxiosError';

import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
  RawAxiosRequestHeaders,
} from 'axios';

export interface HttpClientTimingEvent {
  durationMs: number;
  method: string;
  outcome: 'success' | 'error';
  route: string;
  startedAt: number;
  statusCode?: number;
}

export type HttpClientTimingObserver = (event: HttpClientTimingEvent) => void;

type CreateAxiosInstanceReqParams = {
  baseURL: string;
  isCancel?: boolean;
  onHttpTiming?: HttpClientTimingObserver;
  overrideAxiosRequestConfig?: AxiosRequestConfig;
  overrideOnFulfilled?: (response: AxiosResponse) => any;
  requestId?: string;
  token?: string;
};

const DEFAULT_TIMEOUT = 30 * 1000;

type HttpTimingState = {
  method: string;
  route: string;
  startedAt: number;
};

const now = () => globalThis.performance?.now() ?? Date.now();
const roundMilliseconds = (value: number) => Math.round(value * 100) / 100;

/**
 * 외부 HTTP 경로에서 query와 동적 식별자를 제거합니다.
 * 원문 URL, query, 인증 정보는 timing event에 포함하지 않습니다.
 */
const normalizeHttpRoute = (url: string | undefined) => {
  try {
    const pathname = new URL(url || '/', 'http://placeholder.invalid').pathname;
    return (
      pathname
        .split('/')
        .map(segment => {
          if (!segment) return segment;
          return isDynamicRouteSegment(segment) ? ':id' : segment;
        })
        .join('/') || '/'
    );
  } catch {
    return '/';
  }
};

export const createAxiosInstance = (params: CreateAxiosInstanceReqParams) => {
  const {
    baseURL,
    token,
    isCancel,
    onHttpTiming,
    overrideAxiosRequestConfig = {},
    overrideOnFulfilled,
    requestId,
  } = params;
  const { headers: overrideHeaders = {}, ...restOverrideAxiosRequestConfig } =
    overrideAxiosRequestConfig;
  const headers: RawAxiosRequestHeaders = {
    ...(token && { 'x-auth-token': `${token}` }),
    ...(requestId && { [KC_REQUEST_ID_HEADER]: requestId }),
    'content-type': 'application/json; charset=utf-8',
    accept: 'application/json',
  };

  const axiosInstance = axios.create({
    timeout: DEFAULT_TIMEOUT,
    baseURL,
    withCredentials: true,
    proxy: process.env.NODE_ENV === 'test' ? false : undefined,
    ...restOverrideAxiosRequestConfig,
    headers: {
      ...headers,
      ...overrideHeaders,
    },
  });

  if (onHttpTiming) {
    const timingStore = new WeakMap<InternalAxiosRequestConfig, HttpTimingState>();

    axiosInstance.interceptors.request.use(config => {
      timingStore.set(config, {
        method: (config.method || 'GET').toUpperCase(),
        route: normalizeHttpRoute(config.url),
        startedAt: now(),
      });
      return config;
    });

    const emitHttpTiming = (
      config: InternalAxiosRequestConfig | undefined,
      outcome: HttpClientTimingEvent['outcome'],
      statusCode?: number,
    ) => {
      if (!config) return;

      const timing = timingStore.get(config);
      if (!timing) return;
      timingStore.delete(config);

      try {
        onHttpTiming({
          ...timing,
          durationMs: roundMilliseconds(now() - timing.startedAt),
          outcome,
          statusCode,
        });
      } catch {
        // 관측 코드 오류가 실제 API 요청의 성공/실패 흐름을 바꾸지 않도록 격리합니다.
        return;
      }
    };

    axiosInstance.interceptors.response.use(
      response => {
        emitHttpTiming(response.config, 'success', response.status);
        return response;
      },
      (error: AxiosError) => {
        emitHttpTiming(error.config, 'error', error.response?.status);
        return Promise.reject(error);
      },
    );
  }

  const onFulfilled =
    overrideOnFulfilled ||
    ((response: AxiosResponse) => {
      return response.data ?? {};
    });

  const onRejected = (error: AxiosError) => {
    const customAxiosError = new CustomAxiosError(error);
    captureAxiosError(customAxiosError);
    const { response } = error;

    const { status, data } = response ?? { status: STATUS_CODE.INTERNAL_SERVER_ERROR };

    let refinedError = error;

    // network error 또는 status : 500
    if (typeof data === 'undefined' || status === STATUS_CODE.INTERNAL_SERVER_ERROR) {
      refinedError = { ...refinedError, message: STATUS_CODE.INTERNAL_SERVER_ERROR.toString() };
    }

    // token unauthorized
    if (status === STATUS_CODE.UNAUTHORIZED) {
      refinedError = { ...refinedError, message: STATUS_CODE.UNAUTHORIZED.toString() };
    }

    if (status === STATUS_CODE.FORBIDDEN) {
      refinedError = { ...refinedError, message: STATUS_CODE.FORBIDDEN.toString() };
    }

    return Promise.reject(refinedError);
  };

  axiosInstance.interceptors.response.use(onFulfilled, onRejected);

  return axiosInstance;
};
